import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getProfile } from '../services/profileService';
import { getLabDataByName } from '../services/labService';

const XrayResultsContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: 400;
    color: #666666;
  }
`;

const Header = styled.div`
  background: white;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Logo = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #666;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const NotificationContainer = styled.div`
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;
  margin-right: 8px;

  &:hover {
    transform: scale(1.1);
  }
`;

const BellIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z' fill='%23666666'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background-color: #ff4444;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 10px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Noto Sans Thai', sans-serif !important;
  min-width: 16px;
  padding: 0 2px;
`;

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const ProfileImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const ProfileName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageHeader = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 18px;
  font-weight: 600;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const NotificationCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const NotificationHeader = styled.div`
  background: #00BBD3;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 400;
`;

const NotificationIcon = styled.div`
  width: 20px;
  height: 20px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const NotificationContent = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const NotificationImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex-shrink: 0;
`;

const NotificationDivider = styled.div`
  width: 1px;
  height: 45px;
  background-color: #d0d0d0;
  flex-shrink: 0;
  margin: 0 4px;
`;

const NotificationText = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.h3`
  font-size: 16px;
  font-weight: 400;
  color: #8C8C8C;
  margin: 0 0 8px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const NotificationDescription = styled.p`
  font-size: 14px;
  color: #8C8C8C;
  line-height: 1.5;
  margin: 0 0 8px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: 400;
`;

const NotificationTime = styled.span`
  font-size: 12px;
  color: #8C8C8C;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: 400;
`;

const XrayResultsPage = () => {
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(0);
  const [userData, setUserData] = useState({
    name: '',
    profileImage: null
  });
  const [xrayReports, setXrayReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleNotificationClick = () => {
    navigate('/notifications');
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await getProfile();
        let profile = response?.data || response?.profile;

        // Get image path and convert to full Firebase URL
        let imagePath = profile.image || profile.profile_image || '';
        let imageUrl = null;
        if (imagePath && imagePath !== '') {
          if (imagePath.startsWith('http')) {
            imageUrl = imagePath;
          } else {
            imageUrl = `https://firebasestorage.googleapis.com/v0/b/diet-z.appspot.com/o/${imagePath}`;
          }
        }

        setUserData({
          name: profile.name || profile.firstname || '',
          profileImage: imageUrl
        });
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };

    const loadXrayReports = async () => {
      try {
        setLoading(true);
        console.log('📊 XrayResults - Loading xray_report data...');
        const response = await getLabDataByName('xray_report');
        console.log('📊 XrayResults - API Response:', response);

        const dataArray = response?.data || [];
        console.log('📊 XrayResults - Data array:', dataArray);

        // Transform data to notifications format
        const reports = dataArray.map((item, index) => ({
          id: item.id || index,
          type: 'xray-result',
          title: 'ผลการตรวจเอ็กเรย์ของคุณ',
          description: 'กดดาวน์โหลด PDF',
          time: item.dateadd || item.created_at || '-',
          pdfData: item.value, // The PDF text/base64/URL from backend
          image: './PDF.png'
        }));

        setXrayReports(reports);
        setNotificationCount(reports.length);
        console.log('✅ XrayResults - Reports loaded:', reports);
      } catch (error) {
        console.error('❌ Error loading xray reports:', error);
        setXrayReports([]);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
    loadXrayReports();
  }, []);

  const handleProfileClick = () => {
    navigate('/profile-edit');
  };

  const BellIconSVG = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" fill="white"/>
    </svg>
  );

  const handleDownloadPDF = (pdfData, reportId) => {
    try {
      console.log('📥 Downloading PDF:', reportId, pdfData);

      // Check if pdfData is a URL
      if (pdfData && pdfData.startsWith('http')) {
        // Direct URL - open in new tab
        window.open(pdfData, '_blank');
        return;
      }

      // Check if it's base64 encoded PDF
      if (pdfData && pdfData.includes('base64')) {
        // Extract base64 data if it has data:application/pdf;base64, prefix
        const base64Data = pdfData.split(',')[1] || pdfData;

        // Convert base64 to blob
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `xray-report-${reportId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      }

      // If it's plain text, assume it's a file path or URL
      if (pdfData) {
        window.open(pdfData, '_blank');
      }
    } catch (error) {
      console.error('❌ Error downloading PDF:', error);
      alert('ไม่สามารถดาวน์โหลด PDF ได้ กรุณาลองใหม่อีกครั้ง');
    }
  };

  return (
    <XrayResultsContainer>
      <Header>
        <HeaderLeft>
          <Logo src="./logo.png" alt="MFU Logo" />
          <HeaderTitle>MFU Wellness Center</HeaderTitle>
        </HeaderLeft>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <NotificationContainer onClick={handleNotificationClick}>
            <BellIcon />
            {notificationCount > 0 && (
              <NotificationBadge>{notificationCount}</NotificationBadge>
            )}
          </NotificationContainer>
          <ProfileContainer onClick={handleProfileClick}>
            <ProfileName>{userData.name || 'ผู้ใช้'}</ProfileName>
            <ProfileImage src={userData.profileImage || './person.png'} alt="Profile" />
          </ProfileContainer>
        </div>
      </Header>

      <PageHeader>
        ผลตรวจเอ็กเรย์และสุขภาพ
      </PageHeader>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#8C8C8C' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : xrayReports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#8C8C8C' }}>
            ไม่พบผลการตรวจเอ็กเรย์
          </div>
        ) : (
          xrayReports.map(report => (
            <NotificationCard
              key={report.id}
              onClick={() => handleDownloadPDF(report.pdfData, report.id)}
              style={{ cursor: 'pointer' }}
            >
              <NotificationHeader>
                <NotificationIcon><BellIconSVG /></NotificationIcon>
                ผลการตรวจเอ็กเรย์
              </NotificationHeader>

              <NotificationContent>
                <NotificationImage src={report.image} alt="" />
                <NotificationDivider />
                <NotificationText>
                  <NotificationTitle>{report.title}</NotificationTitle>
                  <NotificationDescription>{report.description}</NotificationDescription>
                  <NotificationTime>{report.time}</NotificationTime>
                </NotificationText>
              </NotificationContent>
            </NotificationCard>
          ))
        )}
      </Content>
    </XrayResultsContainer>
  );
};

export default XrayResultsPage;